import JSZip from 'jszip';

/**
 * Word on Windows can export a document (getFileAsync) with a relationship whose
 * part was left out of the file, e.g. the add-in task pane link in _rels/.rels:
 *   <Relationship Type=".../webextensiontaskpanes" Target="NULL"/>
 * Word opens such a file, but python-docx on the server does not
 * ("There is no item named 'NULL' in the archive"). This removes every internal
 * relationship whose target part is not in the file. A file with no such
 * relationship is returned unchanged.
 */

const RELATIONSHIP_RE = /<Relationship\b[^>]*?(?:\/>|>[\s\S]*?<\/Relationship>)/g;

function readAttribute(element: string, name: string): string | null {
  const match = element.match(new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`));
  if (!match) return null;
  return match[2] ?? match[3] ?? '';
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Resolves a relationship target to a zip entry name (no leading slash). */
export function resolveRelationshipTarget(relsPath: string, target: string): string {
  // word/_rels/document.xml.rels belongs to word/document.xml, so targets are relative to word/
  const baseDir = relsPath.replace(/(^|\/)_rels\/[^/]*$/, '');
  const raw = target.split('#')[0];
  const joined = raw.startsWith('/') ? raw : (baseDir ? `${baseDir}/${raw}` : raw);
  const parts: string[] = [];
  for (const segment of joined.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') parts.pop();
    else parts.push(segment);
  }
  return safeDecode(parts.join('/'));
}

/** Removes relationships that point to parts missing from the file. */
export async function removeDanglingRelationships(bytes: Uint8Array): Promise<{
  bytes: Uint8Array;
  removed: string[];
}> {
  const zip = await JSZip.loadAsync(bytes);
  // Part names are case-insensitive in Office files
  const entries = new Set(Object.keys(zip.files).map((name) => name.toLowerCase()));
  const removed: string[] = [];

  const relsPaths = Object.keys(zip.files).filter(
    (name) => !zip.files[name].dir && /(^|\/)_rels\/[^/]*\.rels$/i.test(name),
  );

  for (const relsPath of relsPaths) {
    const xml = await zip.files[relsPath].async('string');
    let changed = false;
    const cleaned = xml.replace(RELATIONSHIP_RE, (element) => {
      if ((readAttribute(element, 'TargetMode') || '').toLowerCase() === 'external') return element;
      const target = readAttribute(element, 'Target');
      if (target === null) return element;
      const partName = resolveRelationshipTarget(relsPath, target);
      if (partName && entries.has(partName.toLowerCase())) return element;
      removed.push(`${relsPath}: ${target}`);
      changed = true;
      return '';
    });
    if (changed) zip.file(relsPath, cleaned);
  }

  if (removed.length === 0) return { bytes, removed };
  const output = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
  return { bytes: output, removed };
}
